import { useEffect, useRef, useState } from 'react';

// Files picked/pasted/dropped on the home stage here as chips — nothing navigates,
// the typed draft survives, and they ride along on Send. Page-level paste + drop is
// gated to a CLOSED chat (an open chat owns its own paste/drop surface); text pastes
// fall through untouched, only files stage.
export function useStagedFiles(chatClosed: boolean) {
  const [staged, setStaged] = useState<File[]>([]);

  const stageFiles = (files: File[]) => {
    if (files.length === 0) return;
    setStaged((prev) => [...prev, ...files]);
  };

  const removeAt = (index: number) => setStaged((prev) => prev.filter((_, j) => j !== index));
  const clear = () => setStaged([]);

  const stageRef = useRef(stageFiles);
  stageRef.current = stageFiles;
  useEffect(() => {
    if (!chatClosed) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (files.length === 0) return;
      e.preventDefault();
      stageRef.current(files);
    };
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      stageRef.current(Array.from(e.dataTransfer.files ?? []));
    };
    document.addEventListener('paste', onPaste);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [chatClosed]);

  return { staged, stageFiles, removeAt, clear };
}
